import { nanoid } from 'nanoid/non-secure';

// Prefix for storing campaign session IDs in sessionStorage
const CAMPAIGN_SESSION_PREFIX = 'perspective_campaign_session_';

// Tracks the last page slug shown and the homepage slug per campaign to prevent
// spurious session regeneration (e.g. native form GET in iOS in-app browsers)
const LAST_PAGE_SLUG_PREFIX = 'perspective_last_page_slug_';
const HOMEPAGE_SLUG_PREFIX = 'perspective_homepage_slug_';

// Keep track of the current campaign ID to maintain compatibility with getSessionId()
let currentCampaignId: string | undefined;

export const getSessionId = (): string => {
    if (!currentCampaignId) return '';
    return getSessionIdForCampaign(currentCampaignId) || '';
};

const setSessionId = (campaignId: string, sessionId: string): void => {
    currentCampaignId = campaignId;
    setSessionIdForCampaign(campaignId, sessionId);
};

// Get the sessionStorage key for a specific campaign
const getCampaignSessionKey = (campaignId: string): string => {
    return `${CAMPAIGN_SESSION_PREFIX}${campaignId}`;
};

// Get session ID for a specific campaign
const getSessionIdForCampaign = (campaignId: string): string | undefined => {
    const key = getCampaignSessionKey(campaignId);
    return sessionStorage.getItem(key) || undefined;
};

// Set session ID for a specific campaign
const setSessionIdForCampaign = (campaignId: string, sessionId: string): void => {
    const key = getCampaignSessionKey(campaignId);
    sessionStorage.setItem(key, sessionId);
};

// Called from Page.svelte after variant resolution to track which page is currently shown.
// Stores the actual page slug (accounting for A/B variants) so initSessionId can detect
// spurious re-navigations to the homepage.
export const setCurrentPage = (campaignId: string, pageSlug: string, isHomePage: boolean): void => {
    sessionStorage.setItem(`${LAST_PAGE_SLUG_PREFIX}${campaignId}`, pageSlug);

    if (isHomePage) {
        sessionStorage.setItem(`${HOMEPAGE_SLUG_PREFIX}${campaignId}`, pageSlug);
    }
};

export const initSessionId = (isHomePage: boolean = false, campaignId: string) => {
    // Update the current campaign ID context
    currentCampaignId = campaignId;

    // On the first funnel page, regenerate the session ID —
    // unless the last page shown was the homepage (spurious re-navigation)
    if (isHomePage) {
        const lastPageSlug = sessionStorage.getItem(`${LAST_PAGE_SLUG_PREFIX}${campaignId}`);
        const homepageSlug = sessionStorage.getItem(`${HOMEPAGE_SLUG_PREFIX}${campaignId}`);

        if (
            lastPageSlug &&
            homepageSlug &&
            lastPageSlug === homepageSlug &&
            getSessionIdForCampaign(campaignId)
        ) {
            // The last rendered page was the homepage and a session already exists —
            // this is a spurious re-navigation, skip regeneration
            return;
        }

        // Get existing sessionId from URL params if present
        const urlParams = new URLSearchParams(window.location.search);
        const urlSessionId = urlParams.get('sessionId');

        const newSessionId = urlSessionId || nanoid();
        setSessionId(campaignId, newSessionId);
        return;
    }

    // For secondary pages, check if we have a session for this campaign
    const campaignSessionId = getSessionIdForCampaign(campaignId);

    if (campaignSessionId) {
        // Session exists for this campaign, continue using it
        return;
    }

    // No session exists for this campaign, create a new one
    const urlParams = new URLSearchParams(window.location.search);
    const urlSessionId = urlParams.get('sessionId');

    const newSessionId = urlSessionId || nanoid();
    setSessionId(campaignId, newSessionId);
};
